import type {
  ExportFormat,
  ExportProgress,
  ExportResult,
  GifExportConfig,
  HtmlJsExportConfig,
  ImageExportConfig,
  ReactExportConfig,
} from './types'
import {
  DEFAULT_IMAGE_EXPORT_CONFIG,
  generateFilename,
} from './types'
import { ExportPipeline } from './ExportPipeline'
import { createFrameRenderer } from './FrameRenderer'
import { GifExporter } from './GifExporter'
import { HtmlJsExporter } from './HtmlJsExporter'
import { ImageExporter } from './ImageExporter'
import { ReactExporter } from './ReactExporter'
import type { EffectsState } from '../effects/types'
import type { FullRenderSettings } from '../types'

export class ExportManager {
  private imageExporter: ImageExporter
  private gifExporter: GifExporter
  private htmlJsExporter: HtmlJsExporter
  private reactExporter: ReactExporter
  private exportPipeline: ExportPipeline

  constructor() {
    this.imageExporter = new ImageExporter()
    this.gifExporter = new GifExporter()
    this.htmlJsExporter = new HtmlJsExporter()
    this.reactExporter = new ReactExporter()
    this.exportPipeline = new ExportPipeline({
      frameRenderer: createFrameRenderer(),
    })
  }

  getPipeline(): ExportPipeline {
    return this.exportPipeline
  }

  onProgress(callback: (progress: ExportProgress) => void): void {
    this.gifExporter.onProgress(callback)
  }

  onComplete(callback: (result: ExportResult) => void): void {
    this.gifExporter.onComplete(callback)
  }

  async exportImage(
    canvas: HTMLCanvasElement,
    config: Partial<ImageExportConfig> = {}
  ): Promise<ExportResult> {
    const finalConfig = { ...DEFAULT_IMAGE_EXPORT_CONFIG, ...config }
    const filename = generateFilename('image', finalConfig.format)

    try {
      const blob = await this.imageExporter.export(canvas, finalConfig)

      return {
        success: true,
        filename,
        size: blob.size,
        blob,
        timestamp: Date.now(),
      }
    } catch (error) {
      return {
        success: false,
        filename,
        size: 0,
        error: error instanceof Error ? error.message : 'Image export failed',
        timestamp: Date.now(),
      }
    }
  }

  async exportGif(
    sourceImage: HTMLImageElement | HTMLCanvasElement | ImageData,
    settings: FullRenderSettings,
    effectsState: EffectsState,
    config: Partial<GifExportConfig> = {}
  ): Promise<ExportResult> {
    return this.gifExporter.export(sourceImage, settings, effectsState, config)
  }

  initGifExport(config: Partial<GifExportConfig> = {}): void {
    this.gifExporter.init(config)
  }

  addGifFrame(canvas: HTMLCanvasElement): void {
    this.gifExporter.addFrame(canvas)
  }

  startGifEncoding(): void {
    this.gifExporter.render()
  }

  cancelGifExport(): void {
    this.gifExporter.cancel()
  }

  getGifStatus(): { isCapturing: boolean; isEncoding: boolean; progress: number } {
    return {
      isCapturing: this.gifExporter.isCapturing(),
      isEncoding: this.gifExporter.isEncoding(),
      progress: this.gifExporter.getProgress(),
    }
  }

  async exportHtmlJs(
    canvas: HTMLCanvasElement,
    sourceImage: HTMLImageElement | string,
    settings: FullRenderSettings,
    effectsState: EffectsState,
    config: Partial<HtmlJsExportConfig> = {}
  ): Promise<ExportResult> {
    const filename = generateFilename('html-js')

    try {
      const result = await this.htmlJsExporter.export(
        canvas,
        sourceImage,
        settings,
        effectsState,
        config
      )
      const size = new Blob([
        result.content || '',
        result.externalJsContent || '',
      ]).size

      return {
        success: true,
        filename: result.filename || filename,
        size,
        content: result.content,
        externalJsContent: result.externalJsContent,
        externalJsFilename: result.externalJsFilename,
        needsRuntimeDownload: Boolean(result.externalJsFilename && !result.externalJsContent),
        timestamp: Date.now(),
      }
    } catch (error) {
      return {
        success: false,
        filename,
        size: 0,
        error: error instanceof Error ? error.message : 'HTML+JS export failed',
        timestamp: Date.now(),
      }
    }
  }

  async exportReact(
    sourceImage: HTMLImageElement | string,
    settings: FullRenderSettings,
    effectsState: EffectsState,
    config: Partial<ReactExportConfig> = {}
  ): Promise<ExportResult> {
    const filename = generateFilename('react')

    try {
      const result = await this.reactExporter.export(
        sourceImage,
        settings,
        effectsState,
        config
      )
      const size = new Blob([
        result.content || '',
        result.externalJsContent || '',
        result.typesContent || '',
      ]).size

      return {
        success: true,
        filename: result.filename || filename,
        size,
        content: result.content,
        externalJsContent: result.externalJsContent,
        externalJsFilename: result.externalJsFilename,
        needsRuntimeDownload: Boolean(result.externalJsFilename && !result.externalJsContent),
        typesContent: result.typesContent,
        timestamp: Date.now(),
      }
    } catch (error) {
      return {
        success: false,
        filename,
        size: 0,
        error: error instanceof Error ? error.message : 'React export failed',
        timestamp: Date.now(),
      }
    }
  }

  checkSupport(format: ExportFormat): { supported: boolean; message?: string } {
    switch (format) {
      case 'image':
      case 'gif':
      case 'html-js':
      case 'react':
        return { supported: true }

      default:
        return { supported: false, message: 'Unknown export format' }
    }
  }

  destroy(): void {
    this.gifExporter.destroy()
  }
}

export function createExportManager(): ExportManager {
  return new ExportManager()
}
